import { isDefined, isNotDefined } from '@sgme/fp';
import { Badge, HStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';

import { FormattedNumberWithImpactedDigits } from '@/App/BulkTrade/FormattedNumberWithImpactedDigits.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import type { Color } from '@/components/sg-design-system/types/colors.ts';
import { checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { bulkTradeSlice } from '@/store/bulkTrade/slice.ts';
import type { BulkPricedCashNettedLeg, BulkPricedCashSplitLeg } from '@/store/bulkTrade/state.ts';
import { computeLegData } from '@/store/bulkTrade/utilities/margin.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { ISODate } from '@/types/date.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';

export type BulkPriceTableRowProps = BulkPriceTableNettedRowProps | BulkPriceTableSplitRowProps;

export type BulkPriceTableNettedRowProps = {
  tabId: string;
  isEditable: boolean;
  rowType: 'netted';
  leg: BulkPricedCashNettedLeg;
  precision: number;
  isMarginVisible: boolean;
  negociatedCurrencyIndex: CurrencyIndex;
  negociatedCurrency: string;
  reverseCurrency: string;
  status: 'pending' | 'rejected' | 'done' | 'priced';
};

export type BulkPriceTableSplitRowProps = {
  tabId: string;
  isEditable: boolean;
  rowType: 'splitted';
  leg: BulkPricedCashSplitLeg;
  tradeId?: string;
  nettedLegByDate: Record<ISODate, BulkPricedCashNettedLeg>;
  precision: number;
  isMarginVisible: boolean;
  negociatedCurrencyIndex: CurrencyIndex;
  negociatedCurrency: string;
  reverseCurrency: string;
  status: 'pending' | 'rejected' | 'done' | 'priced';
};

export const BulkPriceTableRow = (props: BulkPriceTableRowProps) => {
  const {
    tabId,
    isEditable,
    rowType,
    leg,
    precision,
    isMarginVisible,
    negociatedCurrencyIndex,
    negociatedCurrency,
    reverseCurrency,
    status,
  } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const dispatch = useAppDispatch();

  const userContext = useUserContext();
  const isSales = checkIfUserInternalSales(userContext);

  const isBuying =
    (leg.quoteLeg.CustomerWay === 'Buy' && negociatedCurrencyIndex === 1) ||
    (leg.quoteLeg.CustomerWay === 'Sell' && negociatedCurrencyIndex === 2);

  const salesWayLabelId = isBuying ? 'bulkTrade.way.clientBuys' : 'bulkTrade.way.clientSells';
  const traderWayLabelId = isBuying ? 'bulkTrade.way.youBuy' : 'bulkTrade.way.youSell';
  const wayLabelId = isSales ? salesWayLabelId : traderWayLabelId;

  const handleChangeSpotCommercialMarginPPS = (value: number | undefined) => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'change spot commercial margin');

    dispatch(bulkTradeSlice.actions.spotCommercialMarginPPSChanged({ tabId, legId: leg.id, value: value ?? 0 }));
  };

  const handleIncrementSpotCommercialMarginPPS = () => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'increment spot commercial margin');

    dispatch(bulkTradeSlice.actions.spotCommercialMarginPPSIncremented({ tabId, legId: leg.id }));
  };

  const handleDecrementSpotCommercialMarginPPS = () => {
    sgwtWebAnalytics?.trackEvent('BulkTrade', 'decrement spot commercial margin');

    dispatch(bulkTradeSlice.actions.spotCommercialMarginPPSDecremented({ tabId, legId: leg.id }));
  };

  // For split legs, detect whether this individual leg runs in the OPPOSITE direction
  // to the netted leg for the same date (e.g. a Sell split inside a net Buy).
  // When opposite, spotMargin ("Spot com.margin PPS") and marginAmount must be negated.
  // Equivalent to fx-web's `wayFactor = nonNettedWay === nettedWay ? +1.0 : -1.0`
  // (connectBulkTradeDetails.ts). Here it's expressed as a clear boolean.
  const isOppositeDirectionSplitLeg =
    rowType === 'splitted' &&
    leg.quoteLeg.CustomerWay !== props.nettedLegByDate[leg.quoteLeg.Date]?.quoteLeg?.CustomerWay;

  const computedValues = computeLegData(
    leg.quoteLeg,
    {
      // spotMargin: for split legs, always use the NETTED leg's margin for the same date.
      // Matches fx-web: `sl.getBulkNettedLegMargin(state, quoteId, leg.paymentDate)` (connectBulkTradeDetails.ts).
      spotMargin:
        rowType === 'netted'
          ? leg.spotCommercialMarginPPS
          : (props.nettedLegByDate[leg.quoteLeg.Date]?.spotCommercialMarginPPS ?? 0),

      // forwardMargin: server-provided ForwardMarginPoints on the quoteLeg.
      // Equivalent to fx-web's `margin.forwardMargin` from the margin store.
      // Used for `impactedDigitsPoints` and `forwardPointsWithMargin`.
      forwardMargin:
        leg.quoteLeg.CustomerWay === 'Buy'
          ? Number(leg.quoteLeg.ForwardMarginPoints?.Ask ?? 0)
          : Number(leg.quoteLeg.ForwardMarginPoints?.Bid ?? 0),
    },
    negociatedCurrencyIndex,
    precision,
    isSales,
    isOppositeDirectionSplitLeg,
  );

  return (
    <tr key={leg.id} data-e2e={`${rowType}-row`}>
      {rowType === 'splitted' && (
        <>
          <td>
            <SplitLegStatusBadge status={status} tradeId={props.tradeId} />
          </td>

          <td>{isDefined(props.tradeId) ? props.tradeId : '-'}</td>
        </>
      )}

      <td data-e2e="product">{leg.quoteLeg.IsForward ? 'Forward' : 'Spot'}</td>

      <td
        data-e2e="way"
        className={clsx({
          'text-turquoise-50': isBuying,
          'text-pink-50': !isBuying,
        })}
      >
        <FormattedMessage id={wayLabelId} values={{ currency: negociatedCurrency }} />
      </td>

      <td className="text-end">
        <span data-e2e="amount">
          <FormattedNumber value={computedValues.amount} />
        </span>{' '}
        <span data-e2e="amountCurrency">{negociatedCurrency}</span>
      </td>

      <td className="p-0">
        <HStack verticalAlignment="center" horizontalAlignment="end">
          <HStack data-e2e="paymentDate" className="p-2" style={{ height: 32 }}>
            <FormattedDate value={computedValues.paymentDate} dateStyle="medium" timeZone="UTC" />
          </HStack>

          <HStack
            data-e2e="paymentDateTenor"
            className="px-2 border-1 border-start"
            horizontalAlignment="center"
            verticalAlignment="center"
            style={{ width: 32, height: 32 }}
          >
            {computedValues.paymentDateTenor}
          </HStack>
        </HStack>
      </td>

      <td data-e2e="spot" className="text-end">
        <FormattedNumberWithImpactedDigits
          value={computedValues.spotWithMargin}
          precision={5}
          impactedDigits={computedValues.impactedDigitsSpot}
        />
      </td>

      <td data-e2e="forwardPoints" className="text-end">
        <FormattedNumberWithImpactedDigits
          value={computedValues.forwardPointsWithMargin}
          precision={2}
          impactedDigits={computedValues.impactedDigitsPoints}
        />
      </td>

      <td data-e2e="allIn" className="text-end">
        <FormattedNumberWithImpactedDigits
          value={computedValues.allInWithMargin}
          precision={5 + 1}
          impactedDigits={computedValues.impactedDigitsAllIn}
        />
      </td>

      {isMarginVisible && (
        <>
          <td className={clsx('text-end', { 'p-0': rowType === 'netted' })}>
            {rowType === 'netted' ? (
              <HStack>
                <SgNumberInput
                  data-e2e="spotMarginInput"
                  value={computedValues.spotMargin}
                  onChange={handleChangeSpotCommercialMarginPPS}
                  maxDecimals={1}
                  readOnly={!isEditable}
                  size="md"
                  signVisible
                  color={getMarginColor(computedValues.spotMargin)}
                  valuePosition="end"
                  className="flex-grow-1 flex-shrink-1"
                />

                {isEditable && (
                  <>
                    <SgButton
                      data-e2e="spotMarginMinusBtn"
                      purpose="default"
                      size="md"
                      onClick={handleDecrementSpotCommercialMarginPPS}
                    >
                      -
                    </SgButton>
                    <SgButton
                      data-e2e="spotMarginPlusBtn"
                      purpose="default"
                      size="md"
                      onClick={handleIncrementSpotCommercialMarginPPS}
                    >
                      +
                    </SgButton>
                  </>
                )}
              </HStack>
            ) : (
              <span data-e2e="spotMargin" className={`text-${getMarginColor(computedValues.spotMargin)}`}>
                <FormattedNumber
                  value={computedValues.spotMargin}
                  minimumFractionDigits={1}
                  maximumFractionDigits={1}
                  signDisplay="always"
                />
              </span>
            )}
          </td>

          <td
            data-e2e="amountMargin"
            className={clsx('text-end', {
              'text-danger': computedValues.marginAmount < 0,
              'text-success': computedValues.marginAmount > 0,
            })}
          >
            <FormattedNumber value={computedValues.marginAmount} signDisplay="always" notation="compact" />{' '}
            {reverseCurrency}
          </td>
        </>
      )}
    </tr>
  );
};

function SplitLegStatusBadge({
  status,
  tradeId,
}: Readonly<{ status: 'pending' | 'rejected' | 'done' | 'priced'; tradeId: string | undefined }>) {
  if (status === 'rejected') {
    return (
      <Badge color="danger" isPill>
        <FormattedMessage id="bulkTrade.table.status.rejected" />
      </Badge>
    );
  }

  if (isNotDefined(tradeId)) {
    return (
      <Badge color="info" isPill>
        <FormattedMessage id="bulkTrade.table.status.pending" />
      </Badge>
    );
  }

  return (
    <Badge color="success" isPill>
      <FormattedMessage id="bulkTrade.table.status.finished" />
    </Badge>
  );
}

/**
 * Returns the color to apply to the margin input based on its value rounded to the displayed precision.
 * Uses the rounded value (not the raw value) so that a value like -0.04 displayed as "0.0"
 * does not get colored as danger.
 *
 * @param value - The raw margin value
 * @returns 'danger' for negative, 'success' for positive, undefined for zero
 */
function getMarginColor(value: number): Color | undefined {
  if (value < 0) {
    return 'danger';
  }

  if (value > 0) {
    return 'success';
  }

  return undefined;
}
